import React, { useState } from "react";
import { ScoreBadge, SECTION_LABELS } from "features/cv-processor/CvProcessorPage";

export interface KeywordEntry {
  term: string;
  category: "must_have_skills" | "nice_to_have_skills" | "tools" | "keywords" | "domain_terms";
  found_in?: string[];
}

export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cache_hit_tokens: number;
}

export interface LogEvent {
  type: "phase_start" | "info" | "llm_input" | "llm_output" | "warning" | "complete" | "error" | "keyword_match" | "pool_addition" | "pool_addition_placement" | "skill_family_match" | "token_usage";
  phase?: string;
  label?: string;
  model?: string;
  message?: string;
  id?: string;
  section?: string;
  system?: string;
  prompt?: string;
  output?: string;
  present?: KeywordEntry[];
  missing?: KeywordEntry[];
  skills?: string[];
  placement?: Record<string, string | null>;
  matches?: Array<{ jd_skill: string; cv_sibling: string; family_name: string; action: "replace" | "add" }>;
  call_type?: string;
  scores?: Record<string, number | null>;
  overall?: number | null;
  score_before?: number;
  score_after?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  cache_hit_tokens?: number;
  outputEvent?: LogEvent;
}

function CollapsibleText({ label, content, labelColor }: { label: string; content: string; labelColor: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 text-xs font-mono ${labelColor} hover:opacity-80 transition-opacity`}
      >
        <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        {label}
      </button>
      {open && (
        <pre className="mt-1 p-2 bg-slate-950 rounded text-xs text-slate-300 font-mono whitespace-pre-wrap break-words overflow-x-auto max-h-64 overflow-y-auto border border-slate-800">
          {content}
        </pre>
      )}
    </div>
  );
}

function tryParseJson(content: string): Record<string, unknown> | null {
  try {
    const trimmed = content.trim();
    const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```$/);
    const jsonStr = fenced ? fenced[1].trim() : trimmed;
    if (!jsonStr.startsWith("{")) return null;
    const parsed = JSON.parse(jsonStr);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function JsonKeyRow({ keyName, value }: { keyName: string; value: unknown }) {
  const [open, setOpen] = useState(false);
  const isSimple = typeof value === "number" || typeof value === "boolean";
  if (isSimple) {
    return (
      <div className="flex gap-2 px-1 py-0.5 text-xs font-mono">
        <span className="text-sky-400">{keyName}:</span>
        <span className="text-emerald-300">{String(value)}</span>
      </div>
    );
  }
  const preview =
    typeof value === "string"
      ? `"${value.slice(0, 80)}${value.length > 80 ? "…" : ""}"`
      : Array.isArray(value)
      ? `[${value.length} item${value.length !== 1 ? "s" : ""}]`
      : "{…}";
  const formatted = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <div className="py-0.5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-mono w-full text-left hover:opacity-80 transition-opacity px-1"
      >
        <span className={`inline-block text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <span className="text-sky-400">{keyName}:</span>
        {!open && (
          <span className="text-muted-foreground truncate ml-1 max-w-xs">{preview}</span>
        )}
      </button>
      {open && (
        <pre className="mt-1 ml-4 p-2 bg-surface rounded text-xs text-foreground font-mono whitespace-pre-wrap break-words overflow-x-auto max-h-64 overflow-y-auto border border-border/50">
          {formatted}
        </pre>
      )}
    </div>
  );
}

function CollapsibleOutput({ content }: { content: string }) {
  const [open, setOpen] = useState(false);
  const parsed = React.useMemo(() => tryParseJson(content), [content]);
  return (
    <div className="mt-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-mono text-emerald-400 hover:opacity-80 transition-opacity"
      >
        <span className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        Output
        {parsed && (
          <span className="text-muted-foreground ml-1">
            (JSON · {Object.keys(parsed).length} keys)
          </span>
        )}
      </button>
      {open && (
        parsed ? (
          <div className="mt-1 p-2 bg-slate-950 rounded border border-slate-800 divide-y divide-slate-800">
            {Object.entries(parsed).map(([k, v]) => (
              <JsonKeyRow key={k} keyName={k} value={v} />
            ))}
          </div>
        ) : (
          <pre className="mt-1 p-2 bg-slate-950 rounded text-xs text-slate-300 font-mono whitespace-pre-wrap break-words overflow-x-auto max-h-64 overflow-y-auto border border-slate-800">
            {content}
          </pre>
        )
      )}
    </div>
  );
}

const CATEGORY_META: Record<string, { label: string; cls: string }> = {
  must_have_skills:    { label: "must-have",    cls: "bg-rose-500/20 text-rose-300 border border-rose-500/30" },
  nice_to_have_skills: { label: "nice-to-have", cls: "bg-amber-500/20 text-amber-300 border border-amber-500/30" },
  tools:               { label: "tool",         cls: "bg-blue-500/20 text-blue-300 border border-blue-500/30" },
  keywords:            { label: "keyword",      cls: "bg-violet-500/20 text-violet-300 border border-violet-500/30" },
  domain_terms:        { label: "domain",       cls: "bg-slate-500/20 text-slate-300 border border-slate-500/30" },
};

const SECTION_META: Record<string, { label: string; cls: string }> = {
  headline:        { label: "title",      cls: "bg-orange-500/20 text-orange-300" },
  summary:         { label: "summary",    cls: "bg-purple-500/20 text-purple-300" },
  work_experience: { label: "experience", cls: "bg-blue-500/20 text-blue-300" },
  skills:          { label: "skills",     cls: "bg-emerald-500/20 text-emerald-300" },
  projects:        { label: "projects",   cls: "bg-teal-500/20 text-teal-300" },
  education:       { label: "education",  cls: "bg-slate-500/20 text-slate-300" },
};

function KeywordMatchLog({ present, missing }: { present: KeywordEntry[]; missing: KeywordEntry[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-2 py-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-mono hover:opacity-80 transition-opacity"
      >
        <span className={`inline-block text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <span className="text-emerald-400">{present.length} present</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-red-400">{missing.length} missing</span>
      </button>
      {open && (
        <div className="mt-2 space-y-3 ml-1">
          {present.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-emerald-400 mb-1.5 font-mono">
                Present ({present.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {present.map((kw, i) => {
                  const cat = CATEGORY_META[kw.category] ?? CATEGORY_META.keywords;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1 bg-emerald-500/5 border border-emerald-500/15 rounded px-1.5 py-0.5"
                    >
                      <span className="text-[11px] text-foreground font-mono">{kw.term}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded font-mono ${cat.cls}`}>{cat.label}</span>
                      {(kw.found_in ?? []).map((s) => {
                        const sec = SECTION_META[s];
                        return sec ? (
                          <span key={s} className={`text-[9px] px-1 py-0.5 rounded font-mono ${sec.cls}`}>
                            {sec.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {missing.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold text-red-400 mb-1.5 font-mono">
                Missing ({missing.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((kw, i) => {
                  const cat = CATEGORY_META[kw.category] ?? CATEGORY_META.keywords;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-1 bg-red-500/5 border border-red-500/15 rounded px-1.5 py-0.5"
                    >
                      <span className="text-[11px] text-muted-foreground font-mono">{kw.term}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded font-mono ${cat.cls}`}>{cat.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function LogPanel({ logs, logsEndRef, enhancing, provider, tokenUsage }: {
  logs: LogEvent[];
  logsEndRef: React.RefObject<HTMLDivElement | null>;
  enhancing: boolean;
  provider: "openai" | "deepseek" | null;
  tokenUsage: TokenUsage | null;
}) {
  const entries = React.useMemo(() => {
    const result: Array<LogEvent & { outputEvent?: LogEvent }> = [];
    const inputMap = new Map<string, number>();
    for (const ev of logs) {
      if (ev.type === "llm_input" && ev.id) {
        inputMap.set(ev.id, result.length);
        result.push({ ...ev });
      } else if (ev.type === "llm_output" && ev.id && inputMap.has(ev.id)) {
        const idx = inputMap.get(ev.id)!;
        result[idx] = { ...result[idx], outputEvent: ev };
      } else {
        result.push(ev);
      }
    }
    return result;
  }, [logs]);

  return (
    <div className="rounded-2xl border border-border bg-surface/50 flex flex-col" style={{ maxHeight: "80vh" }}>
      <div className="flex flex-col px-4 py-3 border-b border-border shrink-0 gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter">AI Processing Logs</h3>
            {provider && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${
                provider === "deepseek"
                  ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                  : "bg-primary/20 text-primary border border-primary/30"
              }`}>
                {provider === "deepseek" ? "DeepSeek" : "OpenAI"}
              </span>
            )}
          </div>
          {enhancing && (
            <span className="text-xs text-primary font-inter animate-pulse">running…</span>
          )}
        </div>
        {tokenUsage && !enhancing && (
          <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-surface-muted border border-border">
              ↑ {tokenUsage.prompt_tokens.toLocaleString()} in
            </span>
            <span className="px-1.5 py-0.5 rounded bg-surface-muted border border-border">
              ↓ {tokenUsage.completion_tokens.toLocaleString()} out
            </span>
            {tokenUsage.cache_hit_tokens > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-surface-muted border border-border text-emerald-500">
                ⚡ {tokenUsage.cache_hit_tokens.toLocaleString()} cached
              </span>
            )}
            <span className="px-1.5 py-0.5 rounded bg-surface-muted border border-border text-foreground">
              {tokenUsage.total_tokens.toLocaleString()} total
            </span>
          </div>
        )}
      </div>
      <div className="overflow-y-auto p-3 space-y-1.5 flex-1 text-xs font-mono">
        {entries.length === 0 && (
          <p className="text-muted-foreground font-inter text-xs">Logs will appear here during enhancement.</p>
        )}
        {entries.map((ev, i) => {
          if (ev.type === "phase_start") {
            return (
              <div key={i} className="mt-2 px-2 py-1.5 rounded bg-primary/10 border border-primary/20">
                <span className="text-primary font-semibold">Phase {ev.phase} — {ev.label}</span>
                {ev.model && <span className="text-muted-foreground ml-2">({ev.model})</span>}
              </div>
            );
          }
          if (ev.type === "info") {
            return <div key={i} className="text-muted-foreground px-2">{ev.message}</div>;
          }
          if (ev.type === "warning") {
            return <div key={i} className="text-yellow-400 px-2">⚠ {ev.message}</div>;
          }
          if (ev.type === "error") {
            return <div key={i} className="text-red-400 px-2">✕ {ev.message}</div>;
          }
          if (ev.type === "llm_input") {
            const isScore = ev.call_type === "score";
            const sectionLabel = ev.section && ev.section !== "all" ? ` · ${SECTION_LABELS[ev.section as keyof typeof SECTION_LABELS] ?? ev.section}` : "";
            const out = ev.outputEvent;
            return (
              <div
                key={i}
                className={`px-2 py-1.5 rounded border ${isScore ? "bg-sky-500/5 border-sky-500/20" : "bg-amber-500/5 border-amber-500/15"}`}
              >
                <div className="flex items-center gap-2">
                  {isScore ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold font-mono bg-sky-500/15 border border-sky-500/30 text-sky-300 tracking-wide">
                      SCORE
                    </span>
                  ) : (
                    <span className="text-amber-400 font-semibold">LLM call</span>
                  )}
                  {sectionLabel && (
                    <span className={`text-xs ${isScore ? "text-sky-400/70" : "text-amber-400/70"}`}>{sectionLabel}</span>
                  )}
                </div>
                <div className="text-muted-foreground text-xs mt-0.5">{ev.model}</div>
                {ev.system && (
                  <CollapsibleText label="System prompt" content={ev.system} labelColor="text-sky-400" />
                )}
                {ev.prompt && (
                  <CollapsibleText label="User prompt" content={ev.prompt} labelColor="text-sky-400" />
                )}
                {out?.output && <CollapsibleOutput content={out.output} />}
                {isScore && out?.scores && Object.keys(out.scores).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-sky-500/15">
                    <div className="grid gap-y-1">
                      {Object.entries(out.scores).map(([key, score]) => (
                        <div key={key} className="flex items-center justify-between gap-3 px-1">
                          <span className="text-muted-foreground text-[11px]">
                            {SECTION_LABELS[key as keyof typeof SECTION_LABELS] ?? key}
                          </span>
                          {score !== null && score !== undefined ? (
                            <ScoreBadge score={score} />
                          ) : (
                            <span className="text-muted-foreground text-[11px]">—</span>
                          )}
                        </div>
                      ))}
                      {out.overall !== null && out.overall !== undefined && (
                        <div className="flex items-center justify-between gap-3 px-1 pt-1 mt-0.5 border-t border-sky-500/10">
                          <span className="text-foreground text-[11px] font-semibold">Overall</span>
                          <ScoreBadge score={out.overall} size="md" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {isScore && out?.score_before !== undefined && out?.score_after !== undefined && (
                  <div className="mt-2 pt-2 border-t border-sky-500/15">
                    <div className="flex items-center gap-2 px-1">
                      <ScoreBadge score={out.score_before} />
                      <span className="text-muted-foreground text-[11px]">→</span>
                      <ScoreBadge score={out.score_after} />
                      {out.score_after !== out.score_before && (
                        <span className={`text-[11px] font-semibold font-mono ${out.score_after > out.score_before ? "text-green-400" : "text-red-400"}`}>
                          {out.score_after > out.score_before ? "+" : ""}{out.score_after - out.score_before}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }
          if (ev.type === "keyword_match") {
            return (
              <div key={i} className="px-2 py-1 rounded bg-surface-muted border border-border/30">
                <KeywordMatchLog present={ev.present ?? []} missing={ev.missing ?? []} />
              </div>
            );
          }
          if (ev.type === "pool_addition") {
            return (
              <div key={i} className="px-3 py-2 rounded bg-emerald-500/8 border border-emerald-500/20">
                <div className="text-[11px] font-semibold text-emerald-400 mb-1.5 font-mono">
                  Skills added from category pool
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(ev.skills ?? []).map((skill) => (
                    <span
                      key={skill}
                      className="text-[11px] px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded font-mono text-emerald-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5 font-mono">
                  These are must-have JD skills matched against the CV category pool. They will be added to the Skills section.
                </p>
              </div>
            );
          }
          if (ev.type === "pool_addition_placement") {
            const placementEntries = Object.entries(ev.placement ?? {});
            return (
              <div key={i} className="px-3 py-2 rounded bg-emerald-500/8 border border-emerald-500/20">
                <div className="text-[11px] font-semibold text-emerald-400 mb-1.5 font-mono">
                  Pool skills placed in Skills section
                </div>
                <div className="flex flex-col gap-1">
                  {placementEntries.map(([skill, heading]) => (
                    <div key={skill} className="flex items-center gap-1.5 text-[11px] font-mono">
                      <span className="px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded text-emerald-300">
                        {skill}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className={heading ? "text-muted-foreground" : "text-muted-foreground italic"}>
                        {heading ?? "not found in skills section"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          if (ev.type === "skill_family_match") {
            const matches = ev.matches ?? [];
            return (
              <div key={i} className="px-3 py-2 rounded bg-amber-500/8 border border-amber-500/20">
                <div className="text-[11px] font-semibold text-amber-400 mb-1.5 font-mono">
                  Skill family match — {matches.length} transferable skill{matches.length !== 1 ? "s" : ""}
                </div>
                <div className="flex flex-col gap-1.5">
                  {matches.map((m) => (
                    <div key={m.jd_skill} className="flex items-center gap-1.5 text-[11px] font-mono flex-wrap">
                      <span className="px-2 py-0.5 bg-amber-500/15 border border-amber-500/25 rounded text-amber-300">
                        {m.jd_skill}
                      </span>
                      <span className="text-muted-foreground">←</span>
                      <span className="text-muted-foreground">{m.cv_sibling}</span>
                      <span className="text-muted-foreground text-[10px]">({m.family_name})</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ml-1 ${
                        m.action === "replace"
                          ? "bg-orange-500/15 text-orange-400 border border-orange-500/25"
                          : "bg-sky-500/15 text-sky-400 border border-sky-500/25"
                      }`}>
                        {m.action === "replace" ? "replace in tech stacks" : "add alongside"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          // token_usage and complete are summarised/handled elsewhere; skip to avoid null gaps.
          return null;
        })}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
}
